/**
 * Analytics event layer. Every app event goes into GTM's dataLayer; the
 * GTM-WS5BKHBX container forwards to GA4, Google Ads and Meta. Tags live in the
 * container, not here, so marketing can rewire without a deploy.
 *
 * ‼ EVERY NEW EVENT GOES IN `EVENTS` FIRST, BEFORE IT IS CALLED ANYWHERE.
 * The const is the contract. medicare-advocates-web shipped two events as raw
 * string literals inside a component, so they never appeared in the list that
 * whoever wires GTM actually reads — and neither ended up with a live trigger.
 * A name that only exists at a call site is a name the container never learns.
 *
 * These names are NOT a proposal: they are read from the live container
 * (2026-10-09), which is already published with matching triggers. Changing one
 * silently detaches it from its tag.
 *
 * ‼ NOTE THE NAME MAPPING: the dataLayer event `lead_submit` drives a GA4 tag
 * that SENDS `generate_lead`. Two different names for one thing, by design.
 * Rename neither.
 *
 * ‼ PII BOUNDARY: never put an NPN, email, phone or name into a dataLayer push.
 * The container carries `npn_present` (a boolean) precisely so Google and Meta
 * see that an NPN existed without receiving it. The real values travel only to
 * PandaDoc, and reach the hub through the PandaDoc webhook.
 */

/** Canonical event names. Mirrored as triggers in GTM-WS5BKHBX. */
export const EVENTS = {
    /** Intake form submitted. GA4 tag sends this as `generate_lead`. */
    LEAD_SUBMIT: "lead_submit",
    /** Signing session opened (PandaDoc document.loaded). */
    CONTRACT_START: "contract_start",
    /** Agent finished signing (PandaDoc document.completed). */
    CONTRACT_COMPLETED: "contract_completed",
    /** Step progression within the contracting funnel. */
    FUNNEL_STEP: "funnel_step",
    /** Agent chose a contracting path. */
    AGENT_TYPE_SELECTED: "agent_type_selected",
    /** Carrier multi-select changed. */
    CARRIERS_SELECTED: "carriers_selected",
    /**
     * Phone verification, Get Contracted. These three were already DORMANT
     * triggers in GTM-WS5BKHBX — read from the live container 2026-10-09 with no
     * product behind them. The OTP step is that product; the names are the
     * container's, not ours.
     *
     * ‼ NEVER attach the phone number or the code to any of them. `otp_sent`
     * carries nothing but the cohort; `otp_failed` carries a REASON STRING, and
     * the reasons are a closed set from `OtpFailure` — not a Twilio message,
     * which can contain the destination number.
     */
    OTP_SENT: "otp_sent",
    OTP_VERIFIED: "otp_verified",
    OTP_FAILED: "otp_failed",
    /**
     * Someone clicked through to register for an event (Eventbrite).
     *
     * ‼ NO TRIGGER IN THE LIVE CONTAINER as of 2026-10-11 — verified with a
     * cache-busted gtm.js and a positive control (`lead_submit` returns 1,
     * `register_click` returns 0). It is in this const because THIS CONST IS
     * THE CONTRACT and an event that exists only at a call site is one GTM
     * never learns. It fires harmlessly into nothing until a trigger and tag
     * are added — see docs/handoff/gtm-otp-events.md for that request shape.
     */
    REGISTER_CLICK: "register_click",
    /**
     * An Eventbrite order COMPLETED in the embedded checkout, on our page.
     *
     * ‼ THIS IS THE ONLY EVENT-SIDE CONVERSION WE CAN ATTRIBUTE. `register_click`
     * is intent — it fires when someone leaves for Eventbrite, and the click IDs
     * do not survive that hop, so Ads never learns which campaign earned the
     * registration. This one fires from the widget's `onOrderComplete` while the
     * visitor is still on our page, with `gclid`/`fbclid` in scope.
     *
     * ‼ `eb_event_id`, NEVER `event_id` — same rule as register_click. `event_id`
     * is the Meta dedup key shared by lead_submit / contract_start /
     * contract_completed.
     *
     * ‼ CARRIES NO ORDER DETAIL AND NO PII. Eventbrite's callback supplies
     * neither, which is convenient: there is no amount, quantity or attendee
     * name available to leak. Value-based bidding would need their webhooks, not
     * this event.
     *
     * ‼ NO TRIGGER IN THE LIVE CONTAINER. Like register_click and
     * newsletter_signup, it fires into nothing until someone with publish rights
     * adds a trigger and tags — see docs/handoff/gtm-eventbrite-conversions.md.
     */
    REGISTER_COMPLETE: "register_complete",
    /**
     * Newsletter signup on /subscribe-to-our-newsletter.
     *
     * ‼ NO TRIGGER IN THE LIVE CONTAINER — not audited for this event, so
     * assume none. It is here because THIS CONST IS THE CONTRACT; it fires
     * harmlessly into nothing until a trigger and tag exist.
     *
     * ‼ CARRIES NO EMAIL. The address is the entire payload of this form, and
     * it goes to the lead pipeline only — never to the dataLayer. There is no
     * parameter on this event that could hold one.
     */
    NEWSLETTER_SIGNUP: "newsletter_signup",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * PandaDoc template -> `packet_type`, confirmed with the owner 2026-10-09.
 * "Agent Boost Marketing - Contract copy" (Independent) -> field
 * "Medicare Advocates - Contract copy"   (Strategic)   -> strategic
 */
export type PacketType = "field" | "strategic";

export function packetTypeFor(agentType: string | undefined): PacketType {
    return String(agentType ?? "").toLowerCase().includes("strategic")
        ? "strategic"
        : "field";
}

/**
 * Dedup id shared by a browser event and its server-side twin, so Meta counts
 * one conversion rather than two. Generate ONCE per submission and reuse it
 * across lead_submit / contract_start / contract_completed for that agent.
 */
export function newEventId(): string {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch {
        // fall through
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

type DataLayerRecord = Record<string, unknown>;

declare global {
    interface Window {
        dataLayer?: DataLayerRecord[];
    }
}

/**
 * Ensure the queue exists before GTM does. GTM is deferred to first interaction
 * (see SiteProviders), so events fired earlier would otherwise be pushed into
 * nothing. An array created here is the same array GTM adopts on load, so those
 * events replay rather than vanish.
 */
function queue(): DataLayerRecord[] | null {
    if (typeof window === "undefined") return null;
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

/** Push an event. Undefined params are dropped so variables stay unset, not blank. */
export function track(event: EventName, params: DataLayerRecord = {}): void {
    const dl = queue();
    if (!dl) return;
    const clean: DataLayerRecord = { event };
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== "") clean[k] = v;
    }
    dl.push(clean);
}

/** Set persistent variables (click IDs, rid) readable by every later event. */
export function setDataLayerVars(vars: DataLayerRecord): void {
    const dl = queue();
    if (!dl || Object.keys(vars).length === 0) return;
    dl.push({ ...vars });
}
